import React from 'react';
import { Grid } from '@mui/material';
import {
  TrendingUp, ReceiptLong, Payments, ShowChart, Warning, School,
} from '@mui/icons-material';
import { FinanceSummary } from '../../../services/financeReportsService';
import { formatMoney } from './chartConfig';
import { useAuth } from '../../../context/AuthContext';
import KpiTile from './KpiTile';

interface KpiGridProps {
  summary: FinanceSummary | null;
  loading: boolean;
}

/**
 * KPI row for Finance Reports — money in, money out, and what is still owed:
 *
 *   Fee collections  — fee receipts in the window
 *   Other income     — Revenue module entries (donations, grants, misc.)
 *   Fines collected  — fine payments
 *   Expenses         — approved expenses (money out)
 *   Net cash flow    — what cash and bank actually moved, from the books
 *   Receivables      — owed today on live fee challans + active fines
 */
const KpiGrid: React.FC<KpiGridProps> = ({ summary, loading }) => {
  const { currency } = useAuth();
  const money = (n: number) => formatMoney(n, currency);
  const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

  const fees = summary?.fees;
  const feesIn = fees?.collected.amount ?? 0;
  const otherIn = summary?.revenue?.total.amount ?? 0;
  const finesIn = summary?.fines?.collected.amount ?? 0;
  const expensesOut = summary?.expenses?.byStatus.approved.amount ?? 0;
  // From the books: also counts refunds, bank charges and count differences the tiles above don't.
  const net = summary?.cash.net ?? 0;
  const owedFees = fees?.outstanding.amount ?? 0;
  const owedFines = summary?.fines?.outstanding.amount ?? 0;

  const tiles: Array<React.ComponentProps<typeof KpiTile>> = [
    { label: 'Fee collections', value: money(feesIn), sub: plural(fees?.collected.count ?? 0, 'receipt'), icon: <School />, tone: 'primary' },
    { label: 'Other income', value: money(otherIn), sub: plural(summary?.revenue?.total.count ?? 0, 'entry', 'entries'), icon: <TrendingUp />, tone: 'success' },
    { label: 'Fines collected', value: money(finesIn), sub: plural(summary?.fines?.collected.count ?? 0, 'payment'), icon: <Payments />, tone: 'warning' },
    { label: 'Expenses', value: money(expensesOut), sub: `${summary?.expenses?.byStatus.approved.count ?? 0} approved`, icon: <ReceiptLong />, tone: 'error' },
    { label: 'Net cash flow', value: money(net), sub: net >= 0 ? 'More in than out' : 'More out than in', icon: <ShowChart />, tone: net >= 0 ? 'success' : 'error' },
    {
      label: 'Receivables',
      value: money(owedFees + owedFines),
      sub: `Fees ${money(owedFees)} · Fines ${money(owedFines)}`,
      icon: <Warning />,
      tone: 'neutral',
    },
  ];

  return (
    <Grid container spacing={2}>
      {tiles.map(t => (
        <Grid key={t.label} size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
          <KpiTile {...t} loading={loading} />
        </Grid>
      ))}
    </Grid>
  );
};

export default KpiGrid;
